import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X, FileText, FileCheck2, Send, PenLine, Lock, Trash2, Upload, Download, Users, Link2,
  Globe, ShieldCheck, AlertTriangle, Loader2, Eye,
} from 'lucide-react';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import ConfirmDialog from '../ui/ConfirmDialog';
import Dialog from '../ui/Dialog';
import Badge from '../ui/Badge';
import { statusTone } from '../ui/tone';
import ReasonModal from '../sales/ReasonModal';
import {
  ClassificationBadge, InlineError, PublishedBadge, Spinner, apiErrorMessage, useDateFormatter,
} from '../sales/shared';
import { DOCUMENT_ACCEPT, checkDocumentFile, formatBytes, isPreviewable } from './documentFiles';
import { CLASSIFICATIONS } from './DocumentUploadModal';
import EntityPicker from '../ui/EntityPicker';
import type {
  DocumentClassification, DocumentLinkEntityType, DocumentStatus, DocumentVersion, OteDocument, User,
} from '../../types';


export function DocumentStatusBadge({ status }: { status: DocumentStatus }) {
  const { t } = useTranslation();
  // Colour from the single status → tone map (DESIGN_SYSTEM.md §2.4); locked adds a lock icon.
  return (
    <Badge tone={statusTone(status)} dot={status !== 'locked'}>
      {status === 'locked' && <Lock className="w-3 h-3" aria-hidden="true" />}
      {t(`documents.status_${status}`)}
    </Badge>
  );
}

/**
 * The document detail side panel: metadata, status/signing, classification &
 * portal publishing, version history (real file uploads, downloads, hashes),
 * entity links, sharing and signature evidence. Shared by the Documents
 * screen and the customer page.
 */
export default function DocumentDetailDrawer({
  documentId,
  onClose,
  onChanged,
  onDeleted,
  canWrite: canWriteProp = true,
}: {
  documentId: string;
  /** D-308 (QX-1319): false hides "delete" and "upload a new version" (the server refuses them anyway). */
  canWrite?: boolean;
  onClose: () => void;
  /** Called after any change so the caller can refresh its listing. */
  onChanged?: () => void;
  onDeleted?: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const fmtDate = useDateFormatter();

  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | { type: 'delete' } | { type: 'transition'; status: DocumentStatus }>(null);
  const [publishTarget, setPublishTarget] = useState<boolean | null>(null);

  const docQuery = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => api.getDocumentBundle(documentId),
  });
  const doc = docQuery.data?.data;
  // D-308c (DQ-292): the server says, per document, what the caller may do (the same rule it enforces).
  // An older server without the flags: the explorer's answer.
  const canWrite = doc?.can_edit !== undefined ? doc.can_edit : canWriteProp;
  const canDelete = doc?.can_delete !== undefined ? doc.can_delete : canWrite;
  const canShare = doc?.can_share !== undefined ? doc.can_share : canWrite;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    onChanged?.();
  };
  const fail = (err: unknown) => setError(apiErrorMessage(err, t('documents.actionError')));

  const transitionMutation = useMutation({
    mutationFn: (status: DocumentStatus) => api.transitionDocumentStatus(documentId, status),
    onSuccess: () => { setConfirm(null); setError(null); refresh(); },
    onError: (err) => { setConfirm(null); fail(err); },
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteDocument(documentId),
    onSuccess: () => { setConfirm(null); onChanged?.(); onDeleted?.(); onClose(); },
    onError: (err) => { setConfirm(null); fail(err); },
  });
  const signMutation = useMutation({
    mutationFn: (versionId: string) =>
      api.signDocumentVersion(documentId, { version_id: versionId, signer_name: user?.name ?? undefined, signer_email: user?.email ?? undefined }),
    onSuccess: () => { setError(null); refresh(); },
    onError: fail,
  });
  const classificationMutation = useMutation({
    mutationFn: (classification: DocumentClassification) => api.updateDocument(documentId, { classification }),
    onSuccess: () => { setError(null); refresh(); },
    onError: fail,
  });
  const publishMutation = useMutation({
    mutationFn: ({ published, reason }: { published: boolean; reason: string }) =>
      api.publishDocument(documentId, published, reason || undefined),
    onSuccess: () => { setPublishTarget(null); setError(null); refresh(); },
  });

  const busy = transitionMutation.isPending || signMutation.isPending || classificationMutation.isPending || deleteMutation.isPending;

  const latest: DocumentVersion | null =
    doc?.latest_version ?? [...(doc?.versions ?? [])].sort((a, b) => b.version_number - a.version_number)[0] ?? null;
  const canSign = !!doc && canWrite && (doc.status === 'ready_to_sign' || doc.status === 'sent') && !!latest;

  return (
    <>
    <Dialog
      open
      onClose={onClose}
      variant="drawer"
      size="xl"
      title={doc?.title ?? t('documents.document')}
      bodyClassName="p-0"
    >
        {docQuery.isLoading ? (
          <Spinner />
        ) : docQuery.isError || !doc ? (
          <div className="p-5 space-y-3">
            <InlineError>{apiErrorMessage(docQuery.error, t('documents.actionError'))}</InlineError>
            <button type="button" className="btn btn-secondary" onClick={onClose}>{t('documents.closePanel')}</button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 pb-4">
              <DocumentStatusBadge status={doc.status} />
              <Badge>{t(`documents.type_${doc.document_type}`)}</Badge>
              <ClassificationBadge classification={doc.classification} />
              {doc.is_published && <PublishedBadge />}
            </div>

            <div className="px-5 py-5 space-y-5">
              <InlineError>{error}</InlineError>

              {/* ── Metadata & status ── */}
              <Section title={t('documents.metadata')}>
                {doc.description && <p className="text-sm text-surface-700 mb-3 whitespace-pre-wrap">{doc.description}</p>}
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  {doc.lead_id && doc.organization_is_internal && doc.lead_name ? (
                    <Meta label={t('documents.lead')} value={doc.lead_name} />
                  ) : (
                    <Meta label={t('documents.organization')} value={doc.organization_name ?? '—'} />
                  )}
                  <Meta label={t('documents.folder')} value={doc.folder_name ?? '—'} />
                  <Meta label={t('documents.createdAt')} value={fmtDate(doc.created_at, 'dd/MM/yyyy HH:mm')} />
                  <Meta label={t('documents.updatedAt')} value={fmtDate(doc.updated_at, 'dd/MM/yyyy HH:mm')} />
                  <Meta label={t('documents.createdBy')} value={doc.created_by_name ?? '—'} />
                </dl>
                <div className="mt-4 flex flex-wrap gap-2 border-t border-surface-200 pt-4">
                  {canWrite && doc.status === 'draft' && (
                    <button type="button" disabled={busy}
                      onClick={() => setConfirm({ type: 'transition', status: 'ready_to_sign' })}
                      className="btn btn-primary">
                      <FileCheck2 className="w-4 h-4" aria-hidden />
                      {t('documents.toReadyToSign')}
                    </button>
                  )}
                  {canWrite && doc.status === 'ready_to_sign' && (
                    <button type="button" disabled={busy}
                      onClick={() => setConfirm({ type: 'transition', status: 'sent' })}
                      className="btn btn-primary">
                      <Send className="w-4 h-4" aria-hidden />
                      {t('documents.toSent')}
                    </button>
                  )}
                  {canSign && (
                    <button type="button" disabled={busy} onClick={() => latest && signMutation.mutate(latest.id)}
                      className={`btn ${doc.status === 'ready_to_sign' ? 'btn-secondary' : 'btn-primary'}`}>
                      {signMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" aria-hidden />}
                      {t('documents.signVersion', { number: latest?.version_number })}
                    </button>
                  )}
                  {canWrite && (doc.status === 'ready_to_sign' || doc.status === 'sent') && !latest && (
                    <p className="text-xs text-surface-600 self-center">{t('documents.signNeedsFile')}</p>
                  )}
                  {canWrite && doc.status === 'signed' && (
                    <button type="button" disabled={busy}
                      onClick={() => setConfirm({ type: 'transition', status: 'locked' })}
                      className="btn btn-secondary">
                      <Lock className="w-4 h-4" aria-hidden />
                      {t('documents.lock')}
                    </button>
                  )}
                  {canDelete && (
                    <button type="button" onClick={() => setConfirm({ type: 'delete' })}
                      className="btn btn-ghost ms-auto text-surface-600 hover:bg-danger-50 hover:text-danger-700">
                      <Trash2 className="w-4 h-4" aria-hidden />
                      {t('documents.deleteDocAction')}
                    </button>
                  )}
                </div>
              </Section>

              {/* ── Visibility: classification + publishing ── */}
              <Section title={t('documents.visibilityTitle')}>
                {!canWrite && (
                  <p className="text-sm text-surface-700">
                    {t('documents.classificationLabel')}: {t(`documents.classification.${doc.classification ?? 'internal'}`)}
                  </p>
                )}
                {canWrite && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                  <div>
                    <label className="label">{t('documents.classificationLabel')}</label>
                    <select className="input" value={doc.classification ?? 'internal'} disabled={classificationMutation.isPending}
                      onChange={(e) => classificationMutation.mutate(e.target.value as DocumentClassification)}>
                      {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{t(`documents.classification.${c}`)}</option>)}
                    </select>
                  </div>
                  <div>
                    <button
                      type="button"
                      disabled={doc.classification !== 'client_visible' || publishMutation.isPending}
                      onClick={() => setPublishTarget(!doc.is_published)}
                      className={`btn w-full ${doc.is_published ? 'btn-secondary' : 'btn-primary'}`}
                    >
                      <Globe className="w-4 h-4" aria-hidden />
                      {doc.is_published ? t('documents.unpublish') : t('documents.publish')}
                    </button>
                  </div>
                </div>
                )}
                <p className="text-xs text-surface-600 mt-2">
                  {doc.classification !== 'client_visible'
                    ? t('documents.publishRequiresClientVisible')
                    : doc.is_published
                      ? t('documents.publishedSince', { date: fmtDate(doc.published_at) })
                      : t('documents.notPublishedHint')}
                </p>
              </Section>

              <VersionsSection
                documentId={documentId}
                doc={doc}
                canWrite={canWrite}
                onChanged={refresh}
                onError={fail}
              />

              <LinksSection documentId={documentId} organizationId={doc.organization_id} links={doc.links} canWrite={canWrite} onChanged={refresh} onError={fail} />

              <PermissionsSection documentId={documentId} doc={doc} canShare={canShare} onChanged={refresh} onError={fail} />

              {/* ── Signature evidence ── */}
              {(doc.signatures?.length ?? 0) > 0 && (
                <Section title={t('documents.signaturesTitle')}>
                  <ul className="divide-y divide-surface-300/40">
                    {doc.signatures!.map((s) => {
                      const v = doc.versions?.find((x) => x.id === s.version_id);
                      return (
                        <li key={s.id} className="py-2 text-sm">
                          <div className="flex items-center justify-between gap-2">
                            <span className="inline-flex items-center gap-1.5 font-medium text-surface-900">
                              <ShieldCheck className="w-4 h-4 text-success-600" aria-hidden />
                              {s.signer_name || s.signer_email || '—'}
                            </span>
                            <span className="text-xs text-surface-600">{fmtDate(s.signed_at, 'dd/MM/yyyy HH:mm')}</span>
                          </div>
                          <p className="text-xs text-surface-600 mt-0.5 flex flex-wrap gap-x-3">
                            {v && <span>{t('documents.versionShort', { number: v.version_number })}</span>}
                            {s.provider && <span>{t('documents.signatureProvider')}: {s.provider}</span>}
                            <span>{t(`documents.signatureStatus.${s.status}`, s.status)}</span>
                            {s.content_hash && (
                              <span dir="ltr" className="font-mono" title={s.content_hash}>sha256 {s.content_hash.slice(0, 12)}…</span>
                            )}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                </Section>
              )}
            </div>
          </>
        )}
    </Dialog>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.type === 'transition'
          ? t('documents.transitionTitleNamed', { title: doc?.title ?? '', status: t(`documents.status_${confirm.status}`) })
          : t('documents.deleteDocTitleNamed', { title: doc?.title ?? '' })}
        description={confirm?.type === 'transition' ? t('documents.transitionConsequence') : t('documents.deleteDocConsequence')}
        confirmLabel={confirm?.type === 'transition' ? t('documents.transitionAction') : t('documents.deleteDocAction')}
        danger={confirm?.type === 'delete'}
        loading={transitionMutation.isPending || deleteMutation.isPending}
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.type === 'delete') deleteMutation.mutate();
          else transitionMutation.mutate(confirm.status);
        }}
        onCancel={() => setConfirm(null)}
      />

      {publishTarget !== null && doc && (
        <ReasonModal
          title={publishTarget ? t('documents.publish') : t('documents.unpublish')}
          description={publishTarget ? t('documents.publishConfirm') : t('documents.unpublishConfirm')}
          confirmLabel={publishTarget ? t('documents.publish') : t('documents.unpublish')}
          reasonLabel={t('documents.reasonOptional')}
          loading={publishMutation.isPending}
          error={publishMutation.isError ? apiErrorMessage(publishMutation.error, t('documents.actionError')) : null}
          onConfirm={(reason) => publishMutation.mutate({ published: publishTarget, reason })}
          onCancel={() => { setPublishTarget(null); publishMutation.reset(); }}
        />
      )}
    </>
  );
}

/* ── Versions ────────────────────────────────────────────────────────── */

function VersionsSection({
  documentId,
  doc,
  canWrite,
  onChanged,
  onError,
}: {
  documentId: string;
  doc: OteDocument;
  canWrite: boolean;
  onChanged: () => void;
  onError: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  const fmtDate = useDateFormatter();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: () => api.uploadDocumentVersion(documentId, file!, note.trim() || undefined),
    onSuccess: () => {
      setFile(null);
      setNote('');
      setOpen(false);
      if (fileRef.current) fileRef.current.value = '';
      onChanged();
    },
    onError,
  });

  const preview = async (v: DocumentVersion) => {
    setDownloadingId(v.id);
    try {
      await api.previewDocumentVersion(documentId, v.id);
    } catch (err) {
      onError(err);
    } finally {
      setDownloadingId(null);
    }
  };

  const download = async (v: DocumentVersion) => {
    setDownloadingId(v.id);
    try {
      await api.downloadDocumentVersion(documentId, v.id, v.file_name);
    } catch (err) {
      onError(err);
    } finally {
      setDownloadingId(null);
    }
  };

  const pick = (f: File | null) => {
    if (!f) { setFile(null); setFileProblem(null); return; }
    const p = checkDocumentFile(f);
    if (p) {
      setFile(null);
      setFileProblem(p === 'type' ? t('documents.upload.badType') : t('documents.upload.tooLarge'));
      return;
    }
    setFileProblem(null);
    setFile(f);
  };

  const versions = [...(doc.versions ?? [])].sort((a, b) => b.version_number - a.version_number);
  const locked = doc.status === 'locked';

  return (
    <Section
      title={t('documents.versions')}
      actions={canWrite && !locked && !open && (
        <button type="button" onClick={() => setOpen(true)}
          className="btn btn-secondary btn-sm">
          <Upload className="w-3.5 h-3.5" aria-hidden />
          {t('documents.uploadVersion')}
        </button>
      )}
    >
      {locked && <p className="text-xs text-surface-600 mb-3">{t('documents.lockedNoVersions')}</p>}
      {open && !locked && (
        <div className="mb-4 rounded-lg border border-surface-200 bg-surface-50 p-3 space-y-3">
          <p className="text-xs text-surface-600">{t('documents.versionsKeptHint')}</p>
          {/* Q-DQ-297: a new version of a published document reaches the customer at once. */}
          {doc.is_published && <p className="text-xs font-medium text-warning-700" role="note">{t('documents.publishedVersionWarning')}</p>}
          {doc.status === 'signed' && (
            <p className="text-xs text-warning-900 bg-warning-50 border border-warning-200 rounded-lg px-2 py-1.5 flex gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              {t('documents.signedNewVersionWarning')}
            </p>
          )}
          <input ref={fileRef} type="file" accept={DOCUMENT_ACCEPT} className="input"
            onChange={(e) => pick(e.target.files?.[0] ?? null)} />
          {file && <p className="text-xs text-surface-600">{file.name} · {formatBytes(file.size)}</p>}
          {fileProblem && <p className="text-xs text-danger-700">{fileProblem}</p>}
          <input className="input" value={note} placeholder={t('documents.versionNote')} onChange={(e) => setNote(e.target.value)} />
          <div className="flex gap-2 justify-end">
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setOpen(false); setFile(null); setFileProblem(null); }}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-primary btn-sm" disabled={!file || uploadMutation.isPending}
              onClick={() => uploadMutation.mutate()}>
              {uploadMutation.isPending ? t('common.uploading') : t('common.upload')}
            </button>
          </div>
        </div>
      )}
      {versions.length === 0 ? (
        <p className="text-sm text-surface-500 text-center py-4">{t('documents.noVersions')}</p>
      ) : (
        <ul className="divide-y divide-surface-300/40">
          {versions.map((v) => (
            <li key={v.id} className="flex items-center justify-between gap-2 py-2">
              <div className="flex items-start gap-2 min-w-0">
                <FileText className="w-4 h-4 shrink-0 text-surface-500 mt-0.5" aria-hidden />
                <div className="min-w-0">
                  <p className="text-sm text-surface-900 truncate">
                    <span className="font-semibold me-1.5">{t('documents.versionShort', { number: v.version_number })}</span>
                    {/* DQ-293: a mixed Hebrew / English file name keeps its own direction. */}
                    <bdi dir="auto">{v.file_name}</bdi>
                  </p>
                  <p className="text-xs text-surface-600 flex flex-wrap gap-x-2">
                    <span>{fmtDate(v.created_at, 'dd/MM/yyyy HH:mm')}</span>
                    {v.uploaded_by_name && <span>{v.uploaded_by_name}</span>}
                    <span>{formatBytes(v.file_size)}</span>
                    {v.hash_sha256 && (
                      <span dir="ltr" className="font-mono" title={v.hash_sha256}>sha256 {v.hash_sha256.slice(0, 12)}…</span>
                    )}
                  </p>
                  {v.version_note && <p className="text-xs text-surface-600 mt-0.5">{v.version_note}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {v.is_signed && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-success-700">
                    <FileCheck2 className="w-4 h-4" aria-hidden />
                    {t('documents.signed')}
                  </span>
                )}
                {isPreviewable(v.file_name) && (
                  <button type="button" className="p-1.5 rounded-lg text-primary-700 hover:bg-primary-50 disabled:opacity-40"
                    aria-label={t('documents.preview')} title={t('documents.preview')}
                    disabled={downloadingId === v.id} onClick={() => preview(v)}>
                    <Eye className="w-4 h-4" />
                  </button>
                )}
                <button type="button" className="p-1.5 rounded-lg text-primary-700 hover:bg-primary-50 disabled:opacity-40"
                  aria-label={t('common.download')} title={t('common.download')}
                  disabled={downloadingId === v.id} onClick={() => download(v)}>
                  {downloadingId === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/* ── Links ───────────────────────────────────────────────────────────── */

const LINK_TYPES: DocumentLinkEntityType[] = ['project', 'quote', 'contract', 'engagement', 'ticket'];

function LinksSection({
  documentId,
  organizationId,
  links,
  canWrite,
  onChanged,
  onError,
}: {
  documentId: string;
  organizationId: string;
  links?: OteDocument['links'];
  canWrite: boolean;
  onChanged: () => void;
  onError: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState<DocumentLinkEntityType | ''>('');
  const [entityId, setEntityId] = useState('');

  const optionsQuery = useQuery({
    queryKey: ['document-link-options', organizationId, entityType],
    enabled: !!entityType,
    queryFn: async (): Promise<{ id: string; label: string }[]> => {
      switch (entityType) {
        case 'project': {
          const r = await api.getProjects({ organization_id: organizationId, limit: 200 });
          return (r.data ?? []).map((p) => ({ id: p.id, label: p.name }));
        }
        case 'quote': {
          const r = await api.getQuotes({ organization_id: organizationId, limit: 200 });
          return (r.data ?? []).map((q) => ({ id: q.id, label: `#${q.quote_number} · ${q.title}` }));
        }
        case 'contract': {
          const r = await api.listContracts({ organization_id: organizationId });
          return (r.data ?? []).map((c) => ({ id: c.id, label: c.title }));
        }
        case 'engagement': {
          const r = await api.getEngagements({ organization_id: organizationId, limit: 200 });
          return (r.data ?? []).map((e) => ({ id: e.id, label: `${e.child_name || e.id} · ${t(`engagementDetail.types.${e.type}`)}` }));
        }
        case 'ticket': {
          const r = await api.getOrganizationTickets(organizationId, { limit: 100 });
          return (r.data ?? []).map((tk) => ({ id: tk.id, label: tk.title }));
        }
        default:
          return [];
      }
    },
  });

  const addMutation = useMutation({
    mutationFn: () => api.addDocumentLink(documentId, { entity_type: entityType as DocumentLinkEntityType, entity_id: entityId }),
    onSuccess: () => { setEntityId(''); onChanged(); },
    onError,
  });
  const removeMutation = useMutation({
    mutationFn: (linkId: string) => api.removeDocumentLink(documentId, linkId),
    onSuccess: onChanged,
    onError,
  });

  const linked = new Set((links ?? []).filter((l) => l.entity_type === entityType).map((l) => l.entity_id));
  const options = (optionsQuery.data ?? []).filter((o) => !linked.has(o.id));
  const hrefFor = (type: DocumentLinkEntityType, id: string) =>
    type === 'project' ? `/projects/${id}`
      : type === 'quote' ? `/quotes/${id}`
        : type === 'engagement' ? `/engagements/${id}`
          : type === 'ticket' ? `/tickets/${id}`
            : type === 'lead' ? `/leads/${id}`
              : `/organizations/${organizationId}?tab=contracts&contract=${id}`;

  return (
    <Section title={t('documents.links.title')}>
      {(links?.length ?? 0) === 0 ? (
        <p className="text-sm text-surface-500 mb-3">{t('documents.links.empty')}</p>
      ) : (
        <ul className="divide-y divide-surface-300/40 mb-3">
          {links!.map((l) => (
            <li key={l.id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
              <span className="inline-flex items-center gap-1.5 min-w-0">
                <Link2 className="w-3.5 h-3.5 text-surface-500 shrink-0" aria-hidden />
                <span className="text-xs px-1.5 py-0.5 rounded-xs bg-surface-100 text-surface-600 shrink-0">
                  {t(`documents.links.type.${l.entity_type}`)}
                </span>
                <Link to={hrefFor(l.entity_type, l.entity_id)} className="text-primary-700 hover:underline truncate">
                  {l.entity_label || t(`documents.links.type.${l.entity_type}`)}
                </Link>
              </span>
              {canWrite && (
                <button type="button" className="p-1 rounded-xs text-danger-700 hover:bg-danger-50 disabled:opacity-40"
                  aria-label={t('common.remove')} title={t('common.remove')}
                  disabled={removeMutation.isPending} onClick={() => removeMutation.mutate(l.id)}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canWrite && (
      <div className="flex flex-wrap gap-2 items-end">
        <select className="input w-auto min-w-36" value={entityType}
          onChange={(e) => { setEntityType(e.target.value as DocumentLinkEntityType | ''); setEntityId(''); }}
          aria-label={t('documents.links.entityType')}>
          <option value="">{t('documents.links.entityType')}</option>
          {LINK_TYPES.map((ty) => <option key={ty} value={ty}>{t(`documents.links.type.${ty}`)}</option>)}
        </select>
        {entityType && (
          <div className="flex-1 min-w-48">
            <EntityPicker
              value={entityId}
              onChange={setEntityId}
              aria-label={t('documents.links.entity')}
              loading={optionsQuery.isLoading}
              disabled={!optionsQuery.isLoading && options.length === 0}
              placeholder={!optionsQuery.isLoading && options.length === 0 ? t('documents.links.noOptions') : t('documents.links.entity')}
              options={options.map((o) => ({ value: o.id, label: o.label }))}
            />
          </div>
        )}
        <button type="button" className="btn btn-secondary btn-sm" disabled={!entityType || !entityId || addMutation.isPending}
          onClick={() => addMutation.mutate()}>
          <Link2 className="w-3.5 h-3.5" aria-hidden />
          {t('documents.links.add')}
        </button>
      </div>
      )}
    </Section>
  );
}

/* ── Sharing (per-user permissions) ─────────────────────────────────── */

function PermissionsSection({
  documentId,
  doc,
  canShare,
  onChanged,
  onError,
}: {
  documentId: string;
  doc: OteDocument;
  canShare: boolean;
  onChanged: () => void;
  onError: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  const [grantUserId, setGrantUserId] = useState('');
  const [grantView, setGrantView] = useState(true);
  const [grantEdit, setGrantEdit] = useState(false);

  const usersQuery = useQuery({
    queryKey: ['users', 'documents-grant'],
    queryFn: async () => {
      const res = await api.getUsers({});
      return (res.data ?? []) as User[];
    },
  });
  const users = usersQuery.data ?? [];

  const grantMutation = useMutation({
    mutationFn: () =>
      api.grantDocumentPermission(documentId, { user_id: grantUserId || null, employee_id: null, can_view: grantView, can_edit: grantEdit }),
    onSuccess: () => { setGrantUserId(''); onChanged(); },
    onError,
  });

  return (
    <Section title={t('documents.permissions')}>
      {canShare && (
      <div className="flex gap-2 mb-3 items-end flex-wrap">
        <div className="flex-1 min-w-40">
          <EntityPicker
            value={grantUserId}
            onChange={setGrantUserId}
            aria-label={t('documents.selectUser')}
            placeholder={t('documents.selectUser')}
            loading={usersQuery.isLoading}
            options={users.map((u) => ({ value: u.id, label: u.name, secondary: u.email ?? undefined, keywords: u.email ?? undefined }))}
          />
        </div>
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={grantView} onChange={(e) => setGrantView(e.target.checked)} />
          {t('documents.canView')}
        </label>
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={grantEdit} onChange={(e) => setGrantEdit(e.target.checked)} />
          {t('documents.canEdit')}
        </label>
        <button type="button" disabled={!grantUserId || grantMutation.isPending} onClick={() => grantMutation.mutate()}
          className="btn btn-secondary btn-sm">
          <Users className="w-3.5 h-3.5" aria-hidden />
          {t('documents.grant')}
        </button>
      </div>
      )}
      {(doc.permissions?.length ?? 0) === 0 ? (
        <p className="text-sm text-surface-500 text-center py-2">{t('documents.noPermissions')}</p>
      ) : (
        <ul className="divide-y divide-surface-300/40">
          {doc.permissions!.map((p) => (
            <li key={p.id} className="flex items-center justify-between py-2 text-sm">
              <span className="text-surface-900">{p.grantee_name ?? '—'}</span>
              <span className="flex items-center gap-3 text-xs text-surface-600">
                {p.can_view && <span>{t('documents.canView')}</span>}
                {p.can_edit && <span>{t('documents.canEdit')}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/* ── Presentational helpers ─────────────────────────────────────────── */

function Section({ title, actions, children }: { title: string; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="border-t border-surface-200 pt-5 first:border-t-0 first:pt-0">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-surface-900">{title}</h3>
        {actions}
      </div>
      {children}
    </section>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-surface-600">{label}</dt>
      <dd className="text-sm text-surface-900">{value}</dd>
    </div>
  );
}
